import { useReducedMotion, useTransform, type MotionValue } from 'framer-motion'
import { screens, useMediaQuery } from './useMediaQuery'

/**
 * Eén bouwsteen voor diepte op de homepage: elke laag krijgt een eigen uitslag, gekoppeld aan
 * hoe ver een blok door het scherm is gescrold (`progress`, 0 → 1). Zo voelt het overal gelijk.
 *
 * - Telefoon en tablet (onder `lg`): de halve uitslag. Grote verschuivingen voelen op een klein
 *   scherm onrustig.
 * - "Minder beweging": `undefined`, dus geen `style` en alles staat op zijn eindplek.
 * - Alleen transform en opacity: die laat de browser op de grafische kaart rekenen.
 */

/** De uitslag na de schermregel. Puur, voor de test. */
export const layerRange = (range: readonly [number, number], compact: boolean): [number, number] =>
  compact ? [range[0] / 2, range[1] / 2] : [range[0], range[1]]

/** `true` op telefoon en tablet. */
export function useCompactMotion(): boolean {
  return !useMediaQuery(screens.lg)
}

/** Verschuiving in px over de hele `progress`. Negatief = omhoog (een laag dichterbij). */
export function useLayerY(progress: MotionValue<number>, range: readonly [number, number]): MotionValue<number> | undefined {
  const reduce = useReducedMotion()
  const compact = useCompactMotion()
  const y = useTransform(progress, [0, 1], layerRange(range, compact))
  return reduce ? undefined : y
}

/** Een willekeurige overgang (schaal, doorzichtigheid) die bij "minder beweging" wegvalt. */
export function useLayerValue(progress: MotionValue<number>, input: number[], output: number[]): MotionValue<number> | undefined {
  const reduce = useReducedMotion()
  const value = useTransform(progress, input, output)
  return reduce ? undefined : value
}
